/**
 * Централизованный API клиент для работы с бэкендом
 */

import { fetchWithErrorHandling } from './ApiErrorHandler';
import { useAppStore } from '../../stores/useAppStore';
import { trackApiCall } from '../../utils/errorReporting';
import { getImpersonationTicket } from '../../features/impersonation/impersonationStorage';

// ============================================================================
// API CONFIGURATION
// ============================================================================

/**
 * Базовый URL для API
 * 
 * Логика определения:
 * 1. Если VITE_API_BASE_URL задана - используется она (Azure dev/prod окружения)
 * 2. Иначе используется '/api' - для локальной разработки с Vite proxy
 *    (Vite proxy проксирует /api на localhost:3000, см. vite.config.ts)
 */
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL && import.meta.env.VITE_API_BASE_URL !== 'undefined')
  ? import.meta.env.VITE_API_BASE_URL
  : '/api';

/**
 * Дефолтные опции для всех запросов
 */
const DEFAULT_TIMEOUT = 60000; // 60 секунд

/**
 * Генерирует базовые заголовки с correlationId для трассировки.
 * Возвращает headers и correlationId отдельно для использования в трекинге.
 */
async function getDefaultHeaders(): Promise<{ headers: Record<string, string>; correlationId: string }> {
  const correlationId = crypto.randomUUID();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-correlation-id': correlationId,
  };

  const getAccessToken = useAppStore.getState().getAccessToken;

  if (getAccessToken) {
    try {
      const token = await getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else if (import.meta.env.DEV) {
        console.warn('⚠️ API: Токен-провайдер вернул null - запрос будет без авторизации');
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error('❌ API: Ошибка получения токена:', errorMessage);
      }
    }
  } else if (import.meta.env.DEV) {
    console.warn('⚠️ API: Токен-провайдер не установлен - запрос будет без авторизации. Убедитесь, что AuthBootstrap инициализирован.');
  }

  const impersonationTicket = getImpersonationTicket();
  if (impersonationTicket) {
    headers['X-Impersonate'] = impersonationTicket;
  }

  return { headers, correlationId };
}

/**
 * Заголовки авторизации (токен, эмуляция, correlationId) без Content-Type —
 * для запросов не через ApiClient: бинарные загрузки и скачивания.
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const { headers } = await getDefaultHeaders();
  delete headers['Content-Type'];
  return headers;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

/**
 * Стандартный формат ответа от нового Express.js бэкенда
 */
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  operationId?: string;
  validationErrors?: Array<{
    field: string;
    message: string;
  }>;
}

// ============================================================================
// API CLIENT
// ============================================================================

/**
 * Основной класс для всех API запросов
 */
export class ApiClient {
  /**
   * GET запрос
   */
  static async get<T>(
    endpoint: string,
    options: RequestInit & { timeout?: number; maxRetries?: number } = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const { headers: defaultHeaders, correlationId } = await getDefaultHeaders();
    const { headers: optionHeaders, ...restOptions } = options;
    const finalHeaders = { ...defaultHeaders, ...optionHeaders };
    const start = performance.now();

    try {
      const result = await fetchWithErrorHandling<T>(url, {
        ...restOptions,
        method: 'GET',
        headers: finalHeaders,
        timeout: options.timeout || DEFAULT_TIMEOUT,
      });
      trackApiCall({ method: 'GET', url: endpoint, duration: performance.now() - start, success: true, statusCode: undefined, correlationId });
      return result;
    } catch (error) {
      const status = (error as { status?: number }).status;
      trackApiCall({ method: 'GET', url: endpoint, duration: performance.now() - start, success: false, statusCode: status, correlationId });
      throw error;
    }
  }

  /**
   * POST запрос
   */
  static async post<T, D = unknown>(
    endpoint: string,
    data?: D,
    options: RequestInit & { timeout?: number; maxRetries?: number } = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const { headers: defaultHeaders, correlationId } = await getDefaultHeaders();
    const { headers: optionHeaders, ...restOptions } = options;
    const start = performance.now();

    try {
      const result = await fetchWithErrorHandling<T>(url, {
        ...restOptions,
        method: 'POST',
        headers: { ...defaultHeaders, ...optionHeaders },
        body: data ? JSON.stringify(data) : undefined,
        timeout: options.timeout || DEFAULT_TIMEOUT,
      });
      trackApiCall({ method: 'POST', url: endpoint, duration: performance.now() - start, success: true, statusCode: undefined, correlationId });
      return result;
    } catch (error) {
      const status = (error as { status?: number }).status;
      trackApiCall({ method: 'POST', url: endpoint, duration: performance.now() - start, success: false, statusCode: status, correlationId });
      throw error;
    }
  }

  /**
   * PUT запрос
   */
  static async put<T, D = unknown>(
    endpoint: string,
    data?: D,
    options: RequestInit & { timeout?: number; maxRetries?: number } = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const { headers: defaultHeaders, correlationId } = await getDefaultHeaders();
    const { headers: optionHeaders, ...restOptions } = options;
    const start = performance.now();

    try {
      const result = await fetchWithErrorHandling<T>(url, {
        ...restOptions,
        method: 'PUT',
        headers: { ...defaultHeaders, ...optionHeaders },
        body: data ? JSON.stringify(data) : undefined,
        timeout: options.timeout || DEFAULT_TIMEOUT,
      });
      trackApiCall({ method: 'PUT', url: endpoint, duration: performance.now() - start, success: true, statusCode: undefined, correlationId });
      return result;
    } catch (error) {
      const status = (error as { status?: number }).status;
      trackApiCall({ method: 'PUT', url: endpoint, duration: performance.now() - start, success: false, statusCode: status, correlationId });
      throw error;
    }
  }

  /**
   * DELETE запрос
   */
  static async delete<T, D = unknown>(
    endpoint: string,
    data?: D,
    options: RequestInit & { timeout?: number; maxRetries?: number } = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const { headers: defaultHeaders, correlationId } = await getDefaultHeaders();
    const { headers: optionHeaders, ...restOptions } = options;
    const start = performance.now();

    try {
      const result = await fetchWithErrorHandling<T>(url, {
        ...restOptions,
        method: 'DELETE',
        headers: { ...defaultHeaders, ...optionHeaders },
        body: data ? JSON.stringify(data) : undefined,
        timeout: options.timeout || DEFAULT_TIMEOUT,
      });
      trackApiCall({ method: 'DELETE', url: endpoint, duration: performance.now() - start, success: true, statusCode: undefined, correlationId });
      return result;
    } catch (error) {
      const status = (error as { status?: number }).status;
      trackApiCall({ method: 'DELETE', url: endpoint, duration: performance.now() - start, success: false, statusCode: status, correlationId });
      throw error;
    }
  }

  /**
   * PATCH запрос
   */
  static async patch<T, D = unknown>(
    endpoint: string,
    data?: D,
    options: RequestInit & { timeout?: number; maxRetries?: number } = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const { headers: defaultHeaders, correlationId } = await getDefaultHeaders();
    const { headers: optionHeaders, ...restOptions } = options;
    const start = performance.now();

    try {
      const result = await fetchWithErrorHandling<T>(url, {
        ...restOptions,
        method: 'PATCH',
        headers: { ...defaultHeaders, ...optionHeaders },
        body: data ? JSON.stringify(data) : undefined,
        timeout: options.timeout || DEFAULT_TIMEOUT,
      });
      trackApiCall({ method: 'PATCH', url: endpoint, duration: performance.now() - start, success: true, statusCode: undefined, correlationId });
      return result;
    } catch (error) {
      const status = (error as { status?: number }).status;
      trackApiCall({ method: 'PATCH', url: endpoint, duration: performance.now() - start, success: false, statusCode: status, correlationId });
      throw error;
    }
  }

  /**
   * Получение базового URL (для отладки)
   */
  static getBaseUrl(): string {
    return API_BASE_URL;
  }
}

// ============================================================================
// CONVENIENCE EXPORTS
// ============================================================================

export const api = ApiClient;
export { API_BASE_URL };


